import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookMarked, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

export default function Glossary() {
  const [terms, setTerms] = useState([]);
  const [guideTitles, setGuideTitles] = useState({});

  useEffect(() => {
    api.get("/glossary").then((r) => setTerms(r.data)).catch(() => {});
    api.get("/guides").then((r) => {
      const map = {};
      r.data.forEach((g) => { map[g.slug] = g.title; });
      setGuideTitles(map);
    }).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-12">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-rose/40 px-4 py-1.5 text-sm font-semibold text-plum">
          <BookMarked className="h-4 w-4 text-teal" /> Glossary
        </span>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl text-plum">Fitness terms, in plain language</h1>
        <p className="mt-3 text-mutedplum text-lg">
          The words you'll hear around strength, mobility, protein and menopause—explained simply, for women 45+.
        </p>
      </div>

      <h2 className="mt-12 font-display text-2xl text-plum">Fitness terms A–Z</h2>
      <dl className="mt-6 space-y-4">
        {terms.map((t, i) => (
          <motion.div
            key={t.slug}
            id={t.slug}
            data-testid={`glossary-term-${t.slug}`}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35, delay: (i % 6) * 0.05 }}
            className="scroll-mt-24 rounded-2xl bg-white border border-rose/30 shadow-card p-5"
          >
            <dt className="font-display text-xl text-plum">{t.term}</dt>
            <dd className="mt-2 text-[15px] text-mutedplum leading-relaxed">{t.definition}</dd>
            {t.related?.length > 0 && (
              <dd className="mt-3 flex flex-wrap gap-2">
                {t.related.map((slug) => (
                  <Link
                    key={slug}
                    to={`/guides/${slug}`}
                    data-testid={`glossary-related-${slug}`}
                    className="inline-flex items-center gap-1 rounded-full bg-rose-soft px-3 py-1 text-xs font-semibold text-teal hover:bg-rose/40 transition-colors"
                  >
                    {guideTitles[slug] || "Read the guide"} <ArrowUpRight className="h-3 w-3" />
                  </Link>
                ))}
              </dd>
            )}
          </motion.div>
        ))}
      </dl>

      <div className="mt-12 rounded-3xl bg-teal text-white p-8 text-center grain">
        <h2 className="font-display text-2xl text-white">Come learn with the Circle</h2>
        <p className="mt-2 text-white/85">Dive deeper in our guides, or ask real women in the community.</p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/guides" className="rounded-full bg-white px-7 py-3 font-semibold text-teal hover:-translate-y-1 transition-transform duration-300" data-testid="glossary-to-guides">Browse guides</Link>
          <Link to="/community" className="rounded-full border-2 border-white/70 px-7 py-3 font-semibold text-white hover:bg-white/10 transition-colors" data-testid="glossary-to-community">Go to community</Link>
        </div>
      </div>
    </div>
  );
}
